import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { PermissionKey } from '@c4ytechnology/authz';
import { Link as RouterLink, useSearchParams } from 'react-router';
import {
  ADMIN_AUDIT_ACTIONS,
  adminConsoleAuditApi,
  adminConsoleUsersApi,
  type AdminAuditListItem,
} from '../api/adminConsole';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { auditFiltersFromSearch, auditFiltersToSearch, buildAuditQuery, type AuditFilters } from '../lib/adminIamQuery';
import { formatIamTimestamp } from '../lib/iamAssignmentStatus';
import { hasAdminPermission } from '../lib/adminPermissions';
import {
  AUDIT_CATEGORY_LABELS,
  NON_ROUTINE_AUDIT_ACTIONS,
  actionsInCategory,
  auditActionMeta,
  auditActorLabel,
  auditDayLabel,
  auditRelativeTime,
  auditSummary,
  categoryForActions,
  describeUserAgent,
  isSystemActor,
  type AuditCategory,
  type StaffNameLookup,
} from '../lib/adminAuditPresentation';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { AuditDiffViewer } from '../components/admin/iam/AuditDiffViewer';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import { exportTruncatedMessage } from '../lib/adminCsvDownload';
import { auditToneChipProps } from '../lib/auditActionChip';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const PAGE_SIZE = 50;
/** URL flag that adds routine machine records (sign-in account checks, catalog sync) back in. */
const ROUTINE_PARAM = 'routine';
const CATEGORIES = Object.keys(AUDIT_CATEGORY_LABELS) as AuditCategory[];

/**
 * Admin audit log (`/admin/audit-log`, key admin_audit.read). Each row reads as one sentence
 * (who did what to whom) under a day heading; the expanded row holds the before/after diff and the
 * technical record (event code, entity, request id, IP, device). Category chips and actor/date
 * filters cover everyday use; entity and per-action filters sit under "More filters". Routine
 * machine records are hidden unless asked for. Filters live in the URL so user/role pages can
 * deep-link here.
 */
export function AdminConsoleAuditPage() {
  const theme = useTheme();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useMemo(() => auditFiltersFromSearch(searchParams.toString()), [searchParams]);
  const includeRoutine = searchParams.get(ROUTINE_PARAM) === '1';
  const [draft, setDraft] = useState<AuditFilters>(applied);
  const [open, setOpen] = useState<string | null>(null);
  const [moreFilters, setMoreFilters] = useState(
    () => !!(applied.entityType || applied.entityId) || (applied.actions?.length ?? 0) > 0 && !categoryForActions(applied.actions ?? []),
  );

  useEffect(() => setDraft(applied), [applied]);

  /** No actions picked → everything except routine records (unless the toggle is on). */
  const effective = useMemo<AuditFilters>(
    () =>
      (applied.actions?.length ?? 0) === 0 && !includeRoutine ? { ...applied, actions: NON_ROUTINE_AUDIT_ACTIONS } : applied,
    [applied, includeRoutine],
  );

  const q = useInfiniteQuery({
    queryKey: ['adminConsoleAudit', effective],
    queryFn: ({ pageParam }) => adminConsoleAuditApi.list(buildAuditQuery(effective, pageParam, PAGE_SIZE)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: hasToken,
  });

  // Staff names for rows that only carry a user id (deletions, workstation events).
  const meQ = useAdminConsoleMe();
  const canReadUsers = hasAdminPermission(meQ.data, PermissionKey.IAM_USERS_READ);
  const usersQ = useQuery({
    queryKey: ['adminConsoleAudit', 'staffNames'],
    queryFn: () => adminConsoleUsersApi.list({ limit: 100 }),
    enabled: hasToken && canReadUsers,
    staleTime: 5 * 60_000,
  });
  const names = useMemo<StaffNameLookup>(
    () => new Map((usersQ.data?.items ?? []).map((u) => [u.id, u.email || u.displayName])),
    [usersQ.data],
  );

  const exportCsv = useMutation({
    mutationFn: () => adminConsoleAuditApi.exportCsv(buildAuditQuery(effective)),
  });

  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const activeCategory = categoryForActions(applied.actions ?? []);

  const writeUrl = (f: AuditFilters, routine = includeRoutine) => {
    const p = auditFiltersToSearch(f);
    if (routine) p.set(ROUTINE_PARAM, '1');
    setSearchParams(p, { replace: true });
  };
  const apply = () => writeUrl(draft);
  const clear = () => setSearchParams(new URLSearchParams(), { replace: true });
  const pickCategory = (c: AuditCategory | null) => writeUrl({ ...applied, actions: c ? actionsInCategory(c) : [] });

  const headerEnd = useMemo(
    () => (
      <Button
        size="small"
        variant="outlined"
        startIcon={exportCsv.isPending ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
        disabled={exportCsv.isPending}
        onClick={() => exportCsv.mutate()}
      >
        Export CSV
      </Button>
    ),
    [exportCsv],
  );

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to the admin console to view compliance audit history.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Admin audit log"
        subtitle="Who changed users, roles, vendors and settings, and every staff sign-in."
        endAdornment={headerEnd}
      />

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }} role="group" aria-label="Event category">
          <Chip
            label="All events"
            color={activeCategory === null && (applied.actions?.length ?? 0) === 0 ? 'primary' : 'default'}
            variant={activeCategory === null && (applied.actions?.length ?? 0) === 0 ? 'filled' : 'outlined'}
            onClick={() => pickCategory(null)}
          />
          {CATEGORIES.filter((c) => actionsInCategory(c).length > 0).map((c) => (
            <Chip
              key={c}
              label={AUDIT_CATEGORY_LABELS[c]}
              color={activeCategory === c ? 'primary' : 'default'}
              variant={activeCategory === c ? 'filled' : 'outlined'}
              onClick={() => pickCategory(activeCategory === c ? null : c)}
            />
          ))}
        </Stack>

        {applied.subjectUserId && (
          <Chip
            size="small"
            sx={{ mb: 1.5 }}
            label={`About: ${names.get(applied.subjectUserId) ?? applied.subjectUserId}`}
            onDelete={() => writeUrl({ ...applied, subjectUserId: '' })}
          />
        )}

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          <TextField
            size="small"
            label="Performed by (email contains)"
            value={draft.actorEmail ?? ''}
            onChange={(e) => setDraft({ ...draft, actorEmail: e.target.value })}
          />
          <TextField
            size="small"
            type="date"
            label="From"
            value={draft.from ?? ''}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={draft.to ?? ''}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          {moreFilters && (
            <>
              <TextField
                select
                size="small"
                label="Specific events"
                value={[...(draft.actions ?? [])]}
                onChange={(e) => {
                  const v = e.target.value as unknown;
                  setDraft({ ...draft, actions: Array.isArray(v) ? v : String(v).split(',').filter(Boolean) });
                }}
                slotProps={{
                  select: {
                    multiple: true,
                    renderValue: (v) => {
                      const arr = v as string[];
                      if (arr.length === 0) return 'All';
                      return arr.length <= 2 ? arr.map((a) => auditActionMeta(a).label).join(', ') : `${arr.length} events`;
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              >
                {ADMIN_AUDIT_ACTIONS.map((a) => (
                  <MenuItem key={a} value={a} dense>
                    <Checkbox size="small" checked={(draft.actions ?? []).includes(a)} />
                    <ListItemText
                      primary={auditActionMeta(a).label}
                      secondary={AUDIT_CATEGORY_LABELS[auditActionMeta(a).category]}
                      slotProps={{ primary: { fontSize: '0.8rem' }, secondary: { fontSize: '0.7rem' } }}
                    />
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Record type"
                placeholder="e.g. admin_user, iam_role"
                value={draft.entityType ?? ''}
                onChange={(e) => setDraft({ ...draft, entityType: e.target.value })}
              />
              <TextField
                size="small"
                label="Record id"
                value={draft.entityId ?? ''}
                onChange={(e) => setDraft({ ...draft, entityId: e.target.value })}
              />
            </>
          )}

          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ gridColumn: { lg: '1 / -1' } }}>
            <Button type="submit" variant="contained" size="small">
              Apply filters
            </Button>
            <Button size="small" onClick={clear}>
              Clear
            </Button>
            <Button size="small" startIcon={<TuneRoundedIcon />} onClick={() => setMoreFilters((v) => !v)}>
              {moreFilters ? 'Fewer filters' : 'More filters'}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Sign-in account checks and permission-catalog syncs the system writes on every sign-in and deploy.">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeRoutine}
                    onChange={(e) => writeUrl(applied, e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Show routine system entries</Typography>}
              />
            </Tooltip>
          </Stack>
        </Box>
      </Card>

      <IamErrorAlert error={q.error ?? exportCsv.error} sx={{ mb: 2 }} />
      {exportTruncatedMessage(exportCsv.data) && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => exportCsv.reset()}>
          {exportTruncatedMessage(exportCsv.data)}
        </Alert>
      )}

      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        {q.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" sx={adminDataTableSx(theme)}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ width: 110 }}>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell sx={{ width: { md: 260 }, display: { xs: 'none', sm: 'table-cell' } }}>Performed by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => {
                const day = auditDayLabel(row.occurredAt);
                const newDay = i === 0 || auditDayLabel(rows[i - 1].occurredAt) !== day;
                const meta = auditActionMeta(row.action);
                const isOpen = open === row.id;
                return (
                  <Fragment key={row.id}>
                    {newDay && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            bgcolor: alpha(theme.palette.text.primary, 0.03),
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            py: 0.75,
                          }}
                        >
                          {day}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : row.id)}>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={isOpen ? 'Hide details' : 'Show details'}
                          aria-expanded={isOpen}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpen(isOpen ? null : row.id);
                          }}
                        >
                          {isOpen ? (
                            <KeyboardArrowUpRoundedIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                        <Tooltip title={formatIamTimestamp(row.occurredAt)}>
                          <span>{auditRelativeTime(row.occurredAt)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.5, sm: 1.25 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                          <Chip label={meta.label} size="small" {...auditToneChipProps(meta.tone)} />
                          <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            {auditSummary(row, names)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', display: { xs: 'none', sm: 'table-cell' } }}>
                        {isSystemActor(row) ? (
                          <Chip label={auditActorLabel(row, names)} size="small" variant="outlined" />
                        ) : (
                          auditActorLabel(row, names)
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <AuditRowDetails row={row} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No audit events match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} event{rows.length === 1 ? '' : 's'} loaded
          {!includeRoutine && (applied.actions?.length ?? 0) === 0 ? ' · routine system entries hidden' : ''}
        </Typography>
        {q.hasNextPage && (
          <Button size="small" variant="outlined" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

/** Expanded row: what changed, then the technical record support needs to trace the event. */
function AuditRowDetails({ row }: { row: AdminAuditListItem }) {
  const device = describeUserAgent(row.userAgent);
  const aboutUser = ['admin_user', 'workstation', 'loan_reviewer'].includes(row.entityType) ? row.entityId : null;
  const hasChanges = row.beforeState != null || row.afterState != null;
  return (
    <Box
      sx={{
        py: 2,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(260px, 1fr)' },
        // On a phone the diff table scrolls on its own instead of widening the whole log.
        maxWidth: { xs: 'calc(100vw - 48px)', sm: 'none' },
        overflowX: 'auto',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          What changed
        </Typography>
        {hasChanges ? (
          <AuditDiffViewer before={row.beforeState} after={row.afterState} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No details were recorded for this event.
          </Typography>
        )}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Record
        </Typography>
        <Stack gap={0.75}>
          <DetailLine label="Exact time">{formatIamTimestamp(row.occurredAt)}</DetailLine>
          <DetailLine label="Event code" mono copy={row.action}>
            {row.action}
          </DetailLine>
          <DetailLine label="Record" mono copy={`${row.entityType}:${row.entityId}`}>
            {row.entityType}:{row.entityId}
          </DetailLine>
          <DetailLine label="Request id" mono copy={row.requestId ?? undefined}>
            {row.requestId ?? '—'}
          </DetailLine>
          <DetailLine label="IP address" mono>
            {row.ipAddress ?? '—'}
          </DetailLine>
          <DetailLine label="Device">
            {device ? <Tooltip title={row.userAgent ?? ''}><span>{device}</span></Tooltip> : '—'}
          </DetailLine>
        </Stack>
        {aboutUser && (
          <Button
            size="small"
            sx={{ mt: 1.5, px: 0 }}
            component={RouterLink}
            to={`/admin/audit-log?${new URLSearchParams({ subjectUserId: aboutUser }).toString()}`}
          >
            All activity about this user
          </Button>
        )}
      </Box>
    </Box>
  );
}

function DetailLine({
  label,
  children,
  mono,
  copy,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
  copy?: string;
}) {
  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 84, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontFamily: mono ? MONO : undefined, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={typeof children === 'string' ? children : undefined}
      >
        {children}
      </Typography>
      {copy && (
        <Tooltip title="Copy">
          <IconButton size="small" aria-label={`Copy ${label.toLowerCase()}`} onClick={() => void navigator.clipboard?.writeText(copy)}>
            <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
